// Exam detail panel (docs/05-raster-grading.md §2,§8). Holds the project's Exam
// PDF (tasks + material) and an Analyze action that asks Claude to detect the
// tasks + operators; the operator → Anforderungsbereich → Teilaufgabe mapping is
// applied server-side (deterministic). Each detected task gets a card with an
// editable operator and a Teilaufgabe dropdown; confirmed tasks define the graded
// content criteria.

import { useMemo, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, apiJson } from "../../api/client";
import { useExamTasks, useRasterCatalog } from "../../api/hooks";
import type { ExamTask, ExamTasksResponse, Project, RasterCatalog, RasterCriterion } from "../../api/types";
import { toast } from "../../lib/toast";
import { pollOcrProgress } from "../../lib/ocrProgress";
import { useOcrWatch } from "../../lib/useOcrWatch";
import { useExamUiStore } from "../../state/examUiStore";
import { DeleteExamModal } from "./DeleteExamModal";

import "./ExamPanel.css";

const PROJECTS_KEY = "/api/projects/?archived=0";

export function ExamPanel({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { data: catalog } = useRasterCatalog();
  const { data: tasksData, mutate: mutateTasks } = useExamTasks(project.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingText, setPendingText] = useState("");
  const [busy, setBusy] = useState<null | "upload" | "delete">(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const hasExam = !!project.exam_pdf_path;
  const hasContent = !!(pendingFile || pendingText.trim());
  const tasks = tasksData?.tasks ?? [];

  // Mirror the backend signal (server/api/projects.py _is_scan): a scan has no
  // text layer, so exam_text is empty — it needs OCR before analysis.
  const isScan = hasExam && !(project.exam_text ?? "").trim();
  const hasDigitized = !!project.exam_digitized_pdf_path;
  const busyActions = analyzing || ocrRunning;

  // Persistent line under the actions: live status if set, else a standing hint
  // that a scanned exam still needs OCR.
  const statusLine = status ?? (isScan && !hasDigitized ? t("exam_panel.no_digital_text") : null);

  async function handleUpload(file?: File) {
    setBusy("upload");
    try {
      const fd = new FormData();
      const f = file ?? pendingFile;
      if (f) fd.append("file", f);
      else fd.append("file", new File([pendingText.trim()], "exam.txt", { type: "text/plain" }));
      const res = await apiFetch(`/api/projects/${project.id}/set_exam`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await mutate(PROJECTS_KEY);
      toast(t("exam_panel.uploaded"));
      setPendingFile(null);
      setPendingText("");
    } catch {
      toast(t("exam_panel.upload_failed"));
    } finally {
      setBusy(null);
    }
  }

  function handleUploadOrPick() {
    if (hasContent) void handleUpload();
    else fileInputRef.current?.click();
  }

  async function handleDelete() {
    setBusy("delete");
    try {
      await apiJson(`/api/projects/${project.id}/exam`, { method: "DELETE" });
      await mutate(PROJECTS_KEY);
      // Deleting the exam also clears its detected tasks server-side.
      await mutateTasks({ tasks: [] }, { revalidate: false });
      setPendingFile(null);
      setPendingText("");
      setStatus(null);
      toast(t("exam_panel.deleted"));
      setConfirmDelete(false);
    } catch {
      toast(t("exam_panel.delete_failed"));
    } finally {
      setBusy(null);
    }
  }

  // Reconnects the status line to a running OCR job on (re)mount, and watches the
  // job started below. The job runs server-side, so leaving the view and coming
  // back resumes the status instead of losing it.
  const { startWatch } = useOcrWatch({
    projectId: project.id,
    kind: "exam",
    setStatus,
    setRunning: setOcrRunning,
    labels: {
      progress: (page, total) => t("exam_panel.ocr_progress", { page, total }),
      done: (count) => t("exam_panel.ocr_done", { count }),
      failed: t("exam_panel.ocr_failed"),
    },
    onDone: () => mutate(PROJECTS_KEY),
  });

  async function onRunOcr() {
    setOcrRunning(true);
    setStatus(t("exam_panel.sending_to_azure"));
    try {
      // OCR runs in the background (202); the watch polls /ocr_status until it
      // finishes, so a multi-page scan doesn't hold the request open past the
      // proxy timeout. A digital exam returns 409 (scan check is server-side).
      await apiJson(`/api/projects/${project.id}/run_ocr`, { method: "POST" });
      startWatch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setStatus(t("exam_panel.ocr_not_needed"));
      else setStatus(t("exam_panel.ocr_failed"));
      setOcrRunning(false);
    }
  }

  async function onAnalyze() {
    setAnalyzing(true);
    // A scanned exam is auto-OCR'd server-side before detection; poll page
    // progress so that sub-phase shows "page X of Y" like the Run OCR action.
    const needsOcr = isScan && !hasDigitized;
    const stop = needsOcr
      ? pollOcrProgress(project.id, ({ page, total }) => {
          if (total) setStatus(t("exam_panel.ocr_progress", { page: page ?? 0, total }));
        })
      : null;
    try {
      // Surface the OCR pre-step so Kim knows why a scan takes longer to analyse.
      if (needsOcr) {
        setStatus(t("exam_panel.no_digital_text"));
        await new Promise((r) => setTimeout(r, 900));
        setStatus(t("exam_panel.sending_to_azure"));
      } else {
        setStatus(t("exam_panel.detecting_tasks"));
      }
      const fresh = await apiJson<ExamTasksResponse>(`/api/projects/${project.id}/analyze_exam`, {
        method: "POST",
      });
      await mutateTasks(fresh, { revalidate: false });
      await mutate(PROJECTS_KEY); // refresh so the digitized toggle appears
      setStatus(null);
      toast(t("exam_panel.analyzed", { count: fresh.tasks.length }));
    } catch (err) {
      setStatus(null);
      toast((err as Error).message || t("exam_panel.analyze_failed"));
    } finally {
      stop?.();
      setAnalyzing(false);
    }
  }

  async function patchTask(taskId: number, body: Partial<Pick<ExamTask, "operator" | "criterion_key" | "confirmed" | "node_weights" | "weight">>) {
    const fresh = await apiJson<ExamTasksResponse>(`/api/projects/${project.id}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    await mutateTasks(fresh, { revalidate: false });
  }

  async function deleteTask(taskId: number) {
    const fresh = await apiJson<ExamTasksResponse>(`/api/projects/${project.id}/tasks/${taskId}`, {
      method: "DELETE",
    });
    await mutateTasks(fresh, { revalidate: false });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = [...e.dataTransfer.files].find((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".txt") || n.endsWith(".md");
    });
    if (file) {
      setPendingFile(file);
      setPendingText("");
    }
  }

  return (
    <div className="details-content">
      <div className="details-title-row">
        <div className="details-title">{t("exam_panel.title")}</div>
        <div className="exam-actions">
          <button
            type="button"
            className="model-task__generate exam-ocr-btn"
            onClick={() => void onRunOcr()}
            disabled={!hasExam || busyActions}
            title={t("exam_panel.run_ocr_hint")}
          >
            {ocrRunning ? t("exam_panel.running_ocr") : t("exam_panel.run_ocr")}
          </button>
          <button
            type="button"
            className="model-task__generate exam-analyze-btn"
            onClick={() => void onAnalyze()}
            disabled={!hasExam || busyActions}
            title={t("exam_panel.analyze_hint")}
          >
            {analyzing ? t("exam_panel.analyzing") : t("exam_panel.analyze")}
          </button>
        </div>
      </div>
      <div className="details-subtitle">{project.name}</div>

      <p className="details-description">{t("exam_panel.description")}</p>

      {statusLine && (
        <div className={"exam-status" + (busyActions ? " exam-status--busy" : "")} role="status">
          {busyActions && <span className="model-task__spinner" aria-hidden />}
          {statusLine}
        </div>
      )}

      {/* The drag/drop + paste box only appears before an exam exists; once one
          is uploaded it's hidden so the task list has more room — a substitute is
          uploaded straight from the file picker (Upload button below). */}
      {!hasExam && (
        <div
          className={
            "model-upload-zone exam-upload-zone" +
            (dragging ? " model-upload-zone--over" : "") +
            (pendingFile ? " model-upload-zone--has-file" : "")
          }
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
          }}
          onDrop={handleDrop}
        >
          {pendingFile ? (
            <div className="model-upload-file-row">
              <span className="model-upload-file-name">{pendingFile.name}</span>
              <button type="button" className="model-upload-remove" onClick={() => setPendingFile(null)}>
                ×
              </button>
            </div>
          ) : (
            <>
              <textarea
                className="model-upload-textarea exam-upload-textarea"
                placeholder={t("exam_panel.paste_placeholder")}
                value={pendingText}
                onChange={(e) => setPendingText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="model-upload-hint">{t("exam_panel.drag_hint")}</div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            // With an exam present the drop zone is hidden, so a picked substitute
            // uploads immediately; otherwise stage it for the pending-file preview.
            if (hasExam) {
              void handleUpload(file);
            } else {
              setPendingFile(file);
              setPendingText("");
            }
          }
          e.target.value = "";
        }}
      />

      {hasExam ? (
        <div className="model-upload-footer model-upload-footer--split">
          <button type="button" className="model-upload-submit-btn" disabled={busy != null} onClick={handleUploadOrPick}>
            {busy === "upload" ? t("exam_panel.uploading") : t("exam_panel.upload_btn")}
          </button>
          <button type="button" className="model-upload-delete-btn exam-upload-delete-btn" disabled={busy != null} onClick={() => setConfirmDelete(true)}>
            {t("common.delete")}
          </button>
        </div>
      ) : (
        <div className="model-upload-footer">
          <button type="button" className="model-upload-submit-btn" disabled={busy != null} onClick={handleUploadOrPick}>
            {busy === "upload" ? t("exam_panel.uploading") : t("exam_panel.upload_exam_btn")}
          </button>
        </div>
      )}

      <div className="exam-tasks">
        <div className="exam-tasks__heading">{t("exam_panel.tasks_heading")}</div>
        {tasks.length === 0 ? (
          <div className="exam-tasks__empty">{t("exam_panel.no_tasks")}</div>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              catalog={catalog}
              projectNodeWeights={project.rubric_config?.node_weights ?? {}}
              onOperatorPick={(op, key) => void patchTask(task.id, { operator: op, criterion_key: key })}
              onWeightChange={(w) => void patchTask(task.id, { weight: w })}
              onWeightsChange={(nw) => void patchTask(task.id, { node_weights: nw })}
              onDelete={() => void deleteTask(task.id)}
            />
          ))
        )}
      </div>

      {confirmDelete && (
        <DeleteExamModal
          examName={project.name}
          busy={busy === "delete"}
          onConfirm={() => void handleDelete()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

const SM_KEY = "content.sprachmittlung";
// The Aufgabenbereich is fixed by the operator (deterministic). The two AFB III
// operators map to distinct Teilaufgaben (Stellungnahme vs. gestaltend) — the
// criteria list disambiguates them. Sprachmittlung is treated as AFB IV.
const AFB_LABEL: Record<string, string> = {
  "content.afb1_zusammenfassung": "AFB I",
  "content.afb2_analyse": "AFB II",
  "content.afb3_stellungnahme": "AFB III",
  "content.afb3_gestaltend": "AFB III",
  [SM_KEY]: "AFB IV",
};

function applicableCriteria(catalog: RasterCatalog | undefined, key: string): RasterCriterion[] {
  if (!catalog) return [];
  if (key === SM_KEY) return catalog.content_areas.sprachmittlung;
  const all = [...catalog.content_areas.schreiben, ...catalog.content_areas.sprachmittlung];
  const c = all.find((x) => x.key === key);
  return c ? [c] : [];
}

function TaskCard({
  task,
  catalog,
  projectNodeWeights,
  onOperatorPick,
  onWeightChange,
  onWeightsChange,
  onDelete,
}: {
  task: ExamTask;
  catalog: RasterCatalog | undefined;
  projectNodeWeights: Record<string, number>;
  onOperatorPick: (operator: string, criterionKey: string) => void;
  onWeightChange: (weight: number) => void;
  onWeightsChange: (nw: Record<string, number>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const activeTaskId = useExamUiStore((s) => s.activeTaskId);
  const setActiveTaskId = useExamUiStore((s) => s.setActiveTaskId);
  const isActive = activeTaskId === task.id;

  const afbLabel = AFB_LABEL[task.criterion_key] ?? "—";
  const applicable = applicableCriteria(catalog, task.criterion_key);

  // Per-task weight overrides; a node shows the task override, else the project
  // default, else 1.0. Editing a field makes it a task override.
  const overrides = task.node_weights ?? {};
  const weightOf = (key: string) => overrides[key] ?? projectNodeWeights[key] ?? 1;
  const hasOverrides = Object.keys(overrides).length > 0;
  function setWeight(key: string, v: number) {
    onWeightsChange({ ...overrides, [key]: v });
  }

  // Operator dropdown — every catalog operator (which fixes the AFB) plus a
  // Sprachmittlung entry (AFB IV). Picking one sets operator + criterion_key.
  const currentValue = task.criterion_key === SM_KEY ? SM_KEY : task.operator ?? "";
  const options = useMemo(() => {
    const opts = (catalog?.operators ?? []).map((o) => ({
      value: o.term,
      label: o.term,
      operator: o.term,
      criterion_key: o.criterion_key,
    }));
    opts.push({ value: SM_KEY, label: "Sprachmittlung", operator: "Sprachmittlung", criterion_key: SM_KEY });
    // Keep an unrecognised detected operator selectable.
    if (currentValue && !opts.some((o) => o.value === currentValue)) {
      opts.unshift({
        value: currentValue,
        label: task.operator ?? currentValue,
        operator: task.operator ?? "",
        criterion_key: task.criterion_key,
      });
    }
    return opts;
  }, [catalog, currentValue, task.operator, task.criterion_key]);

  // Clicking the task statement toggles only the exam-PDF highlight; the
  // chevron alone toggles the criteria/weights panel.
  function toggleHighlight() {
    setActiveTaskId(isActive ? null : task.id);
  }
  function toggleExpand() {
    setExpanded((v) => !v);
  }

  function onSelect(value: string) {
    const opt = options.find((o) => o.value === value);
    if (opt) onOperatorPick(opt.operator, opt.criterion_key);
  }

  // Reset to the operator Claude detected. The control is always present so it's
  // discoverable, but is active only once the operator differs from the detected
  // one (and a detected operator exists to reset to).
  const detected = task.detected_operator ?? null;
  const canReset = !!detected && detected !== task.operator;
  function onResetOperator() {
    if (!detected) return;
    const opt = options.find((o) => o.operator === detected);
    if (opt) onOperatorPick(opt.operator, opt.criterion_key);
  }

  return (
    <div className="exam-task-card">
      <div className="exam-task-card__top">
        <button
          type="button"
          className={"exam-task-card__task" + (isActive ? " exam-task-card__task--active" : "")}
          onClick={toggleHighlight}
          aria-pressed={isActive}
        >
          {task.label || task.operator || "—"}
        </button>

        <button type="button" className="exam-task-card__delete" onClick={onDelete}>
          {t("common.delete")}
        </button>
      </div>

      <div className="exam-task-card__meta">
        <label className="exam-task-field exam-task-field--weight">
          <span>{t("exam_panel.weight_label")}</span>
          <input
            type="number"
            className="exam-wt-field exam-task-weight"
            min={0}
            step="0.5"
            value={task.weight}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v > 0) onWeightChange(v);
            }}
          />
        </label>

        <label className="exam-task-field exam-task-field--operator">
          <span>{t("exam_panel.operator_label")}</span>
          <div className="exam-operator-row">
            <div className="exam-select-wrap">
              <select
                className="exam-task-select"
                value={currentValue}
                onChange={(e) => onSelect(e.target.value)}
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="exam-select-arrow" aria-hidden>▼</span>
            </div>
            <button
              type="button"
              className="exam-operator-reset"
              onClick={onResetOperator}
              disabled={!canReset}
              title={canReset ? t("exam_panel.reset_operator", { operator: detected }) : t("exam_panel.reset_operator_idle")}
              aria-label={canReset ? t("exam_panel.reset_operator", { operator: detected }) : t("exam_panel.reset_operator_idle")}
            >
              ↺
            </button>
          </div>
        </label>

        <button
          type="button"
          className={"exam-task-card__expand" + (expanded ? " exam-task-card__expand--open" : "")}
          onClick={toggleExpand}
          aria-expanded={expanded}
          title={t("exam_panel.criteria_heading")}
          aria-label={t("exam_panel.criteria_heading")}
        >
          <span className="exam-task-card__chevron" aria-hidden />
        </button>

        <div className="exam-task-field exam-task-field--afb">
          <span>{t("exam_panel.afb_label")}</span>
          <span className="exam-afb-chip">{afbLabel}</span>
        </div>
      </div>

      {expanded && applicable.length > 0 && (
        <div className="exam-task-criteria">
          <div className="exam-task-criteria__teilaufgabe">
            <span className="exam-task-criteria__teilaufgabe-cap">{t("exam_panel.teilaufgabe_label")}</span>
            <span className="exam-task-criteria__teilaufgabe-name">{applicable.map((c) => c.label).join(" · ")}</span>
          </div>
          <div className="exam-task-criteria__head">
            <span className="exam-task-criteria__heading">{t("exam_panel.criteria_heading")}</span>
            {hasOverrides && (
              <button type="button" className="exam-task-criteria__reset" onClick={() => onWeightsChange({})}>
                {t("exam_panel.reset_weights")}
              </button>
            )}
          </div>
          {applicable.map((c) => (
            <div key={c.key} className="exam-crit">
              {c.groups.map((g) => (
                <WeightNodes key={g.key} group={g} weightOf={weightOf} setWeight={setWeight} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Editable weight rows for one criterion group (per-task overrides): a section
// line + indented subcriteria, each with a weight field on the right.
function WeightNodes({
  group,
  weightOf,
  setWeight,
}: {
  group: RasterCatalog["content_areas"]["schreiben"][number]["groups"][number];
  weightOf: (key: string) => number;
  setWeight: (key: string, v: number) => void;
}) {
  if (!group.heading) {
    return (
      <>
        {group.rows.map((r) => (
          <ExamWeightLine key={r.key} label={r.label} level="section" value={weightOf(r.key)} onChange={(v) => setWeight(r.key, v)} />
        ))}
      </>
    );
  }
  return (
    <>
      <ExamWeightLine label={group.heading} level="section" value={weightOf(group.key)} onChange={(v) => setWeight(group.key, v)} />
      {group.rows.map((r) => (
        <ExamWeightLine key={r.key} label={r.label} level="leaf" value={weightOf(r.key)} onChange={(v) => setWeight(r.key, v)} />
      ))}
    </>
  );
}

function ExamWeightLine({
  label,
  level,
  value,
  onChange,
}: {
  label: string;
  level: "section" | "leaf";
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className={"exam-wt-line exam-wt-line--" + level}>
      <span className="exam-wt-line__label">{label}</span>
      <input
        type="number"
        className="exam-wt-field"
        min={0}
        step="0.1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}
